export type MealPreference = 'omni' | 'veg' | 'vegan';

export interface AdditionalGuest {
  name: string;
  meal: MealPreference;
}

export interface RsvpPayload {
  firstName: string;
  lastName: string;
  email: string;
  language: string;
  attending: boolean;
  partySize: number;
  mealPreference: MealPreference;
  additionalGuests: AdditionalGuest[];
  allergies: string;
  nights: 0 | 1 | 2;
  childrenCount: number;
  childrenAges: string;
  note: string;
}

export interface RsvpResponse {
  id: number;
  createdAt: string;
}

export async function submitRsvp(payload: RsvpPayload): Promise<RsvpResponse> {
  const res = await fetch('/api/rsvp', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const body = await res.text().catch(() => '');
    throw new Error(`RSVP save failed (${res.status}): ${body}`);
  }
  return (await res.json()) as RsvpResponse;
}

/* ----- Pexeso leaderboard (shared, server-backed) ----- */

export interface PexesoScoreDto {
  name: string;
  attempts: number;
  firstClicks: number | null;
  bestClicks: number | null;
  skipCount: number;
  firstPlayedAt: string;
  lastPlayedAt: string;
}

export interface PexesoMigrateEntry {
  name: string;
  attempts: number;
  firstClicks: number | null;
  bestClicks: number | null;
  skipCount: number;
  firstPlayedAt: string;
  lastPlayedAt: string;
}

export async function fetchPexesoLeaderboard(): Promise<PexesoScoreDto[]> {
  const res = await fetch('/api/pexeso/leaderboard');
  if (!res.ok) throw new Error(`Leaderboard fetch failed (${res.status})`);
  return (await res.json()) as PexesoScoreDto[];
}

export async function submitPexesoAttempt(
  name: string,
  clicks: number,
  skipped: boolean,
): Promise<PexesoScoreDto> {
  const res = await fetch('/api/pexeso/attempt', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ name, clicks, skipped }),
  });
  if (!res.ok) throw new Error(`Attempt POST failed (${res.status})`);
  return (await res.json()) as PexesoScoreDto;
}

export async function migratePexesoLocal(
  entries: PexesoMigrateEntry[],
): Promise<PexesoScoreDto[]> {
  const res = await fetch('/api/pexeso/migrate', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ entries }),
  });
  if (!res.ok) throw new Error(`Migrate POST failed (${res.status})`);
  return (await res.json()) as PexesoScoreDto[];
}

/* ----- Zážitkový shop (svatební dary) ----- */

export interface ShopItemDto {
  slug: string;
  priceCzk: number;
  priceEur: number;
  stock: number | null;
  remaining: number | null;
  sortOrder: number;
}

export interface CheckoutLine {
  slug: string;
  quantity: number;
}

export interface CheckoutPayload {
  buyerName: string;
  buyerEmail: string;
  message: string;
  language: string;
  lines: CheckoutLine[];
}

export interface CheckoutResultLine {
  slug: string;
  quantity: number;
  price: number;
}

/** QR platba (CZK SPAYD nebo EUR EPC) — jen když má backend nastavený účet. */
export interface CheckoutPayment {
  iban: string;
  bic: string;
  recipient: string;
  amount: number;
  currency: string;
  reference: string;
  qrSvg: string;
}

export interface CheckoutResult {
  buyerName: string;
  currency: string;
  lines: CheckoutResultLine[];
  total: number;
  payment?: CheckoutPayment | null;
}

/** 409 z checkoutu — položky, které se mezitím vyprodaly. */
export class ShopConflictError extends Error {
  soldOut: string[];

  constructor(soldOut: string[]) {
    super('Some items are sold out');
    this.soldOut = soldOut;
  }
}

export async function fetchShopItems(): Promise<ShopItemDto[]> {
  const res = await fetch('/api/shop/items');
  if (!res.ok) throw new Error(`Shop items fetch failed (${res.status})`);
  return (await res.json()) as ShopItemDto[];
}

export async function submitCheckout(payload: CheckoutPayload): Promise<CheckoutResult> {
  const res = await fetch('/api/shop/checkout', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (res.status === 409) {
    const body = (await res.json().catch(() => ({ soldOut: [] }))) as { soldOut?: string[] };
    throw new ShopConflictError(body.soldOut ?? []);
  }
  if (!res.ok) {
    const body = await res.text().catch(() => '');
    throw new Error(`Checkout failed (${res.status}): ${body}`);
  }
  return (await res.json()) as CheckoutResult;
}
